import React, { useCallback, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link as RouterLink } from 'react-router-dom'

import { Box, Card, CardContent, Divider, Link, TableContainer, Typography } from '@mui/material'

import { GRTable, GRTableColumn, SortOrder, TableSortValues } from '../../../../components/GRTable/GRTable'
import { GRTooltip } from '../../../../components/GRTooltip/GRTooltip'
import { DescriptionHoverCard } from '../DescriptionHoverCard'
import { formatAdoptionPct } from '../helpers/catalogueUtils'
import { DEFAULT_TAG_GROUP_COLOR } from '../helpers/tagGroups'

export interface EventCategoryTypeRow {
  id: string
  name: string
  // Event-type page target, built by the page so the segment filters ride along.
  to: string
  state?: unknown
  // Segment-scoped adoption share (0–1), or null when the segment size is unknown.
  adoptionPct: number | null
  // Windowed adopting games, deduplicated across the type's blueprints server-side.
  adoptingGames: number | null
  // Windowed first-time adopters of the type.
  newAdopters: number | null
  // Plain-text glossary definition. The name's hover card omits the definition section when absent.
  description?: string
}

interface EventCategoryTypesCardProps {
  rows: EventCategoryTypeRow[]
  isLoading?: boolean
  // Hover-card breadcrumb. The same for every row, since they all share the page's event category.
  categoryName?: string
  categoryColorHex?: string
}

// Passed as GRTable custom props rather than captured by the column definitions: the
// columns live in state (for sorting), so captured values would go stale when in-app
// navigation reuses the card for another event category.
interface EventCategoryTypesTableProps {
  hoverCardTrail: string[]
  categoryColorHex: string
}

const CENTER_ALIGNED_PROPS = { align: 'center' as const }

/**
 * The event types belonging to one event category, as the drill-down step from
 * the category page to an event-type page. Card header copy + edge-to-edge
 * `GRTable` (the GR "header + table" card pattern), mirroring
 * `EventTypeBlueprintsCard` one level up.
 */
export const EventCategoryTypesCard: React.FC<EventCategoryTypesCardProps> = React.memo(
  ({ rows, isLoading, categoryName, categoryColorHex = DEFAULT_TAG_GROUP_COLOR }) => {
    const { t } = useTranslation()
    const containerRef = useRef<HTMLDivElement>(null)

    const customTableProps: EventCategoryTypesTableProps = useMemo(
      () => ({
        hoverCardTrail: categoryName ? [categoryName] : [],
        categoryColorHex,
      }),
      [categoryName, categoryColorHex]
    )

    const tableColumns: GRTableColumn<EventCategoryTypeRow, EventCategoryTypesTableProps>[] = useMemo(
      () => [
        {
          labelAccessor: t('market-analytics:event_type'),
          accessor: ({ row, customTableProps }) => (
            // Same hover card as the blueprint names on the event-type page: the card sets
            // its own width, so the tooltip's max-width is disabled.
            <GRTooltip
              content={
                <DescriptionHoverCard
                  name={row.name}
                  trail={customTableProps?.hoverCardTrail ?? []}
                  colorHex={customTableProps?.categoryColorHex ?? DEFAULT_TAG_GROUP_COLOR}
                  description={row.description}
                  descriptionLabel={t('market-analytics:detail_panel_what_this_event_type_is')}
                  isRichTextDescription={false}
                />
              }
              disableMaxWidth
              styles={{ minWidth: 0 }}
            >
              <Link component={RouterLink} to={row.to} state={row.state} underline="hover" color="primary" variant="body2" sx={{ fontWeight: 'bold' }}>
                {row.name}
              </Link>
            </GRTooltip>
          ),
          sortable: true,
          sortAccessor: ({ row }) => row.name,
          cellProps: { align: 'left' },
          headerCellProps: { align: 'left', sx: { minWidth: 260 } },
          defaultSortOrder: SortOrder.ASC,
        },
        {
          labelAccessor: () => (
            <GRTooltip content={t('market-analytics:event_type_row_adoption_tooltip')}>{t('market-analytics:detail_stat_adoption_label')}</GRTooltip>
          ),
          accessor: ({ row }) => formatAdoptionPct(row.adoptionPct),
          sortable: true,
          sortAccessor: ({ row }) => (row.adoptionPct === null ? TableSortValues.AlwaysLast : row.adoptionPct),
          cellProps: CENTER_ALIGNED_PROPS,
          headerCellProps: CENTER_ALIGNED_PROPS,
          sortOrder: SortOrder.DESC,
          defaultSortOrder: SortOrder.DESC,
        },
        {
          labelAccessor: () => (
            <GRTooltip content={t('market-analytics:event_type_row_adopting_games_tooltip')}>{t('market-analytics:adopting_games_in_segment')}</GRTooltip>
          ),
          accessor: ({ row }) => (row.adoptingGames === null ? '—' : row.adoptingGames),
          sortable: true,
          sortAccessor: ({ row }) => (row.adoptingGames === null ? TableSortValues.AlwaysLast : row.adoptingGames),
          cellProps: CENTER_ALIGNED_PROPS,
          headerCellProps: CENTER_ALIGNED_PROPS,
          defaultSortOrder: SortOrder.DESC,
        },
        {
          labelAccessor: () => <GRTooltip content={t('market-analytics:event_type_row_new_adopters_tooltip')}>{t('market-analytics:new_adopters')}</GRTooltip>,
          accessor: ({ row }) => (row.newAdopters === null ? '—' : row.newAdopters),
          sortable: true,
          sortAccessor: ({ row }) => (row.newAdopters === null ? TableSortValues.AlwaysLast : row.newAdopters),
          cellProps: CENTER_ALIGNED_PROPS,
          headerCellProps: CENTER_ALIGNED_PROPS,
          defaultSortOrder: SortOrder.DESC,
        },
      ],
      [t]
    )

    const [columns, setColumns] = useState<GRTableColumn<EventCategoryTypeRow, EventCategoryTypesTableProps>[]>(tableColumns)

    const handleColumnsUpdate = useCallback((updatedColumns: GRTableColumn<EventCategoryTypeRow, EventCategoryTypesTableProps>[]) => {
      setColumns(updatedColumns)
    }, [])

    return (
      <Card>
        <CardContent sx={{ pb: 1.5, '&:last-child': { pb: 1.5 } }}>
          <Typography variant="h3" gutterBottom>
            {t('market-analytics:event_category_event_types_title')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('market-analytics:event_category_event_types_description')}
          </Typography>
        </CardContent>
        <Divider />
        <TableContainer component={Box} ref={containerRef}>
          <GRTable
            rows={rows}
            columns={columns}
            customProps={customTableProps}
            onColumnsUpdated={handleColumnsUpdate}
            rowIdKey="id"
            scroller={containerRef}
            isLoading={isLoading}
            noRowsLabel={t('market-analytics:no_data_available')}
            hoverable
            striped
          />
        </TableContainer>
      </Card>
    )
  }
)

EventCategoryTypesCard.displayName = 'EventCategoryTypesCard'
